// Pure, framework-free viewport derivation for the My Polaris keyboard-geometry
// programme. Contains the public viewport types, native `vortex:keyboardgeometry`
// payload validation, and the native / visualViewport / fallback derivations plus
// normalized-state equality. No React, DOM listeners, refs, effects, or CSS
// mutation live here — the provider supplies the current DOM readings as plain
// numbers so every function is deterministic and unit-testable.
//
// @docs:
// - docs/planning/MY_POLARIS_KEYBOARD_GEOMETRY.md

/** Which signal produced the current normalized viewport state. */
export type ViewportSource = 'native-host' | 'visual-viewport' | 'fallback';

/** Public, page-neutral viewport state. All values are CSS pixels (except dpr). */
export interface ViewportState {
  readonly keyboardVisible: boolean;
  readonly keyboardInsetCssPx: number;
  readonly usableHeightCssPx: number;
  readonly viewportWidthCssPx: number;
  readonly viewportHeightCssPx: number;
  readonly source: ViewportSource;
  readonly dpr: number;
}

/**
 * Validated native payload in Android PHYSICAL pixels. Kept private to the
 * bootstrap/provider path; never surfaced on {@link ViewportState}.
 */
export interface NativeKeyboardPayload {
  readonly visible: boolean;
  readonly imeInsetPhysicalPx: number;
  readonly windowHeightPhysicalPx: number;
  readonly windowWidthPhysicalPx: number;
  readonly density: number;
}

/** Native geometry contract identifiers (must match the vortex-webview host). */
export const KEYBOARD_GEOMETRY_EVENT = 'vortex:keyboardgeometry';
export const KEYBOARD_GEOMETRY_VERSION = 1;
export const KEYBOARD_GEOMETRY_SOURCE = 'vortex-webview';

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

/** A safe, strictly-positive device-pixel ratio; neutral `1` when invalid. */
export function safeDpr(dpr: unknown): number {
  return isFiniteNumber(dpr) && dpr > 0 ? dpr : 1;
}

/**
 * Validate a `vortex:keyboardgeometry` event `detail`. Returns the physical
 * payload on success, or `null` for any malformed / partial / wrong-version /
 * wrong-source / non-finite / out-of-range / inconsistent input. Native support
 * begins only once this returns a payload — never inferred from UA/URL/size.
 */
export function parseNativePayload(detail: unknown): NativeKeyboardPayload | null {
  if (detail === null || typeof detail !== 'object') return null;
  const d = detail as Record<string, unknown>;

  if (d.version !== KEYBOARD_GEOMETRY_VERSION) return null; // strict v1; rejects future versions
  if (d.source !== KEYBOARD_GEOMETRY_SOURCE) return null;
  if (typeof d.visible !== 'boolean') return null;

  const { imeInsetPhysicalPx, windowHeightPhysicalPx, windowWidthPhysicalPx, density } = d;
  if (
    !isFiniteNumber(imeInsetPhysicalPx) ||
    !isFiniteNumber(windowHeightPhysicalPx) ||
    !isFiniteNumber(windowWidthPhysicalPx) ||
    !isFiniteNumber(density)
  ) {
    return null;
  }
  if (imeInsetPhysicalPx < 0) return null;
  if (windowHeightPhysicalPx <= 0 || windowWidthPhysicalPx <= 0) return null;
  if (density <= 0) return null;

  // Contract consistency: a hidden keyboard must report a zero inset. Reject an
  // inconsistent hidden-with-positive-inset payload rather than silently fixing a
  // malformed host contract. A visible keyboard with a zero inset stays valid.
  if (d.visible === false && imeInsetPhysicalPx !== 0) return null;

  return Object.freeze({
    visible: d.visible,
    imeInsetPhysicalPx,
    windowHeightPhysicalPx,
    windowWidthPhysicalPx,
    density,
  });
}

/**
 * Derive normalized state from the retained native payload. Physical→CSS
 * conversion uses the page's CURRENT device-pixel ratio; the host `density` is
 * validated but never used as a layout authority. The page's own layout
 * dimensions are the CSS viewport source; physical window dimensions are only a
 * defensive fallback when a page dimension is invalid. The inset keeps fractional
 * precision (no integer rounding).
 */
export function deriveFromNative(
  payload: NativeKeyboardPayload,
  pageDpr: number,
  pageInnerWidth: number,
  pageInnerHeight: number,
): ViewportState {
  const dpr = safeDpr(pageDpr);
  const keyboardInsetCssPx = payload.imeInsetPhysicalPx / dpr;
  const viewportWidthCssPx =
    isFiniteNumber(pageInnerWidth) && pageInnerWidth > 0
      ? pageInnerWidth
      : payload.windowWidthPhysicalPx / dpr;
  const viewportHeightCssPx =
    isFiniteNumber(pageInnerHeight) && pageInnerHeight > 0
      ? pageInnerHeight
      : payload.windowHeightPhysicalPx / dpr;
  return Object.freeze({
    keyboardVisible: payload.visible, // authoritative; not derived from inset height
    keyboardInsetCssPx,
    usableHeightCssPx: Math.max(0, viewportHeightCssPx - keyboardInsetCssPx),
    viewportWidthCssPx,
    viewportHeightCssPx,
    source: 'native-host',
    dpr,
  });
}

/** Current visualViewport readings (plain numbers supplied by the provider). */
export interface VisualViewportReading {
  readonly layoutViewportHeight: number; // documentElement.clientHeight || innerHeight
  readonly visualHeight: number;
  readonly visualWidth: number;
  readonly offsetTop: number;
  readonly scale: number;
}

const SCALE_EPSILON = 0.01;

/**
 * Derive normalized state from `window.visualViewport`. The bottom inset includes
 * `offsetTop` (the portion scrolled above the visual viewport). During pinch zoom
 * (scale != 1) visual-viewport shrinkage is NOT interpreted as keyboard occlusion.
 */
export function deriveFromVisualViewport(r: VisualViewportReading, pageDpr: number): ViewportState {
  const dpr = safeDpr(pageDpr);
  const layoutH = isFiniteNumber(r.layoutViewportHeight) && r.layoutViewportHeight > 0 ? r.layoutViewportHeight : 0;
  const width = isFiniteNumber(r.visualWidth) && r.visualWidth > 0 ? r.visualWidth : layoutH > 0 ? r.visualWidth : 0;
  const zoomed = !isFiniteNumber(r.scale) || Math.abs(r.scale - 1) > SCALE_EPSILON;

  if (zoomed) {
    return Object.freeze({
      keyboardVisible: false,
      keyboardInsetCssPx: 0,
      usableHeightCssPx: layoutH,
      viewportWidthCssPx: Math.max(0, width),
      viewportHeightCssPx: layoutH,
      source: 'visual-viewport',
      dpr,
    });
  }

  const visualH = isFiniteNumber(r.visualHeight) && r.visualHeight >= 0 ? r.visualHeight : layoutH;
  const offsetTop = isFiniteNumber(r.offsetTop) && r.offsetTop >= 0 ? r.offsetTop : 0;
  const keyboardInsetCssPx = Math.max(0, layoutH - visualH - offsetTop);
  return Object.freeze({
    keyboardVisible: keyboardInsetCssPx > 0,
    keyboardInsetCssPx,
    usableHeightCssPx: Math.max(0, layoutH - keyboardInsetCssPx),
    viewportWidthCssPx: Math.max(0, width),
    viewportHeightCssPx: layoutH,
    source: 'visual-viewport',
    dpr,
  });
}

/**
 * Fallback derivation when no valid native payload has arrived and
 * `window.visualViewport` is unavailable. Preserves ordinary browser behaviour:
 * no keyboard signal, zero inset, usable height == viewport height.
 */
export function deriveFallback(
  pageInnerWidth: number,
  pageInnerHeight: number,
  pageDpr: number,
): ViewportState {
  const width = isFiniteNumber(pageInnerWidth) && pageInnerWidth > 0 ? pageInnerWidth : 0;
  const height = isFiniteNumber(pageInnerHeight) && pageInnerHeight > 0 ? pageInnerHeight : 0;
  return Object.freeze({
    keyboardVisible: false,
    keyboardInsetCssPx: 0,
    usableHeightCssPx: height,
    viewportWidthCssPx: width,
    viewportHeightCssPx: height,
    source: 'fallback',
    dpr: safeDpr(pageDpr),
  });
}

/** Structural equality over the seven public fields (suppresses redundant updates). */
export function viewportStatesEqual(a: ViewportState, b: ViewportState): boolean {
  return (
    a.keyboardVisible === b.keyboardVisible &&
    a.keyboardInsetCssPx === b.keyboardInsetCssPx &&
    a.usableHeightCssPx === b.usableHeightCssPx &&
    a.viewportWidthCssPx === b.viewportWidthCssPx &&
    a.viewportHeightCssPx === b.viewportHeightCssPx &&
    a.source === b.source &&
    a.dpr === b.dpr
  );
}
